import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { IconButton, IconName, Stack, Tab, Tabs } from '@optimus-web/core';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { ModalSidePanel, NewTabLink } from '@mews-commander/ui';
import { getRepositoryQueryKey, useRepositoryQuery, UserGroup } from '@mews-commander/data-access';
import { UsersDetails } from './UsersDetails';
import { GeneralInfo } from './GeneralInfo';

interface UserGroupDetailsPanelProps {
    group: UserGroup | null;
    onClose: () => void;
}

enum UserGroupDetailsTab {
    Settings = 'settings',
    Users = 'users',
}

export const UserGroupDetailsPanel = ({ onClose, group }: UserGroupDetailsPanelProps) => {
    const isFetching = !!useIsFetching({
        queryKey: getRepositoryQueryKey('userGroups', 'get', { userGroupId: group?.id }),
    });

    return (
        <ModalSidePanel
            onClose={onClose}
            title={group?.name || ''}
            loading={isFetching}
            content={group && <Content groupId={group.id} />}
            headerActions={
                <NewTabLink url={createGuideLink(ArticlePath.UserGroupsDetailPanel)}>
                    <IconButton variant="tertiary" size="medium" icon={IconName.Help} type="submit" />
                </NewTabLink>
            }
            opened={group != null}
            width="medium"
        />
    );
};

const Content = ({ groupId }: { groupId: string }) => {
    const [activeTab, setActiveTab] = useState<UserGroupDetailsTab>(UserGroupDetailsTab.Settings);

    const { data: activeUserGroup } = useRepositoryQuery('userGroups', 'get', {
        userGroupId: groupId,
    });

    useEffect(() => {
        setActiveTab(UserGroupDetailsTab.Settings);
    }, [groupId]);

    if (!activeUserGroup) {
        return null;
    }

    return (
        <Stack vertical spacing="300">
            <Tabs
                activeTabValue={activeTab}
                onTabClick={(_, tab) => setActiveTab(tab as UserGroupDetailsTab)}
                underline
                inline
                showDivider
            >
                <Tab
                    id={UserGroupDetailsTab.Settings}
                    label={<LocalizedText textKey={TextKey.Settings} />}
                    value={UserGroupDetailsTab.Settings}
                />
                <Tab
                    id={UserGroupDetailsTab.Users}
                    label={<LocalizedText textKey={TextKey.UserPlural} />}
                    value={UserGroupDetailsTab.Users}
                />
            </Tabs>

            {activeTab === 'settings' ? (
                <GeneralInfo group={activeUserGroup} />
            ) : (
                <UsersDetails groupId={activeUserGroup.id} />
            )}
        </Stack>
    );
};
